import { auth, db } from "../../firebase";
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getCookie, setCookie } from "../../util/utils";

const userId = localStorage.getItem("uid");

export const handleLogin = async ({
  email,
  password,
  setLoading,
  navigate,
  setError,
}) => {
  setLoading(true);
  try {
    await signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        navigate("/");
      })
      .catch((error) => {
        setError(error);
      });
  } catch (error) {
    setError(error);
  } finally {
    setLoading(false);
  }
};

async function getUser(email) {
  try {
    const userCollection = collection(db, "users");
    const q = query(userCollection, where("email", "==", email));
    const querySnapshot = await getDocs(q);
    const data = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        return { id: doc.id };
      })
    );
    return data;
  } catch (error) {
    console.error("Error fetching data from URL: ", error.message);
    throw error;
  }
}

export const handleLinkLogin = async (user, email) => {
  try {
    const userId = await getUser(email);
    if (userId.length === 0) {
      await setDoc(doc(db, "users", user.uid), {
        email: email,
        status: "active",
        role: "member",
        isLinkUser: true,
      });
    }
  } catch (error) {
    console.error("Error adding user: ", error);
    throw error;
  }
};

async function getLinkUser(email) {
  try {
    const linkUserCollection = collection(db, "linkUser");
    const q = query(linkUserCollection, where("email", "==", email));
    const querySnapshot = await getDocs(q);
    const data = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        return { id: doc.id };
      })
    );
    return data;
  } catch (error) {
    console.error("Error fetching data from URL: ", error.message);
    throw error;
  }
}

export const updateLinkUser = async (user, email) => {
  try {
    const linkedUserId = await getLinkUser(email);
    const linkUserRef = doc(db, "linkUser", linkedUserId[0].id);
    await updateDoc(linkUserRef, {
      isVerified: true,
      createdAt: Timestamp.fromMillis(String(user.metadata.createdAt)),
      lastLoginAt: Timestamp.fromMillis(String(user.metadata.lastLoginAt)),
    });
  } catch (error) {
    console.error("Error updating user data: ", error);
    throw error;
  }
};

export const handleSignUp = async ({
  email,
  password,
  setLoading,
  navigate,
  setError,
}) => {
  try {
    setLoading(true);
    await createUserWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        const user = userCredential.user;
        await setDoc(doc(db, "users", user.uid), {
          email: email,
          status: "active",
          role: "member",
        });
        navigate("/");
      })
      .catch((error) => {
        setError(error);
      });
  } catch (error) {
    setError(error);
  } finally {
    setLoading(false);
  }
};

export const handleSignOut = async ({ navigate }) => {
  await signOut(auth)
    .then(() => {
      localStorage.removeItem("userKey", null);
      navigate("/");
    })
    .catch((error) => {
      // An error happened.
    });
};

export const handleResetPassword = async ({
  email,
  setLoading,
  setError,
  setSuccess,
}) => {
  try {
    setLoading(true);
    await sendPasswordResetEmail(auth, email)
      .then((userCredential) => {
        setSuccess(true);
      })
      .catch((error) => {
        setError(error);
      });
  } catch (error) {
    setError(error);
  } finally {
    setLoading(false);
  }
};

async function addLinkUser(email) {
  try {
    const linkUserCollectionRef = collection(db, "linkUser");
    await addDoc(linkUserCollectionRef, {
      email,
      emailSentAt: serverTimestamp(),
      emailSend: true,
      isVerified: false,
      status: "active",
    });
  } catch (error) {
    console.error("Error adding link user: ", error);
    throw error;
  }
}

export const handleLinkSignIn = async ({
  email,
  setLinkLoading,
  setEmailSendSuccess,
  setError,
}) => {
  try {
    setLinkLoading(true);
    await sendSignInLinkToEmail(auth, email, {
      // this is the URL that we will redirect back to after clicking on the link in mailbox
      // url: "http://localhost:3000/",
      url: "https://lara.support/",
      handleCodeInApp: true,
    })
      .then(async (res) => {
        // The link was successfully sent. Inform the user.
        // Save the email locally so you don't need to ask the user for it again
        // if they open the link on the same device.
        localStorage.setItem("emailForSignIn", email);
        const linkedUserId = await getLinkUser(email);
        linkedUserId.length === 0 && (await addLinkUser(email));
        setEmailSendSuccess(true);
      })
      .catch((error) => {
        setError(error);
      });
  } catch (error) {
    setError(error);
  } finally {
    setLinkLoading(false);
  }
};

export const changeUserPassword = async ({
  email,
  currentPassword,
  newPassword,
  setShowChangePasswordModal,
  setErrorMessage,
}) => {
  try {
    const user = auth.currentUser;
    const credential = promptForCredentials(email, currentPassword); // Function to get user credentials
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
    setShowChangePasswordModal(false);
  } catch (error) {
    console.error("Error updating password: ", error);
    setErrorMessage(error.message ? error.message : error);
    throw error.message ? error.message : error;
  }
};

const promptForCredentials = (userEmail, currentPassword) => {
  const email = userEmail; //prompt("Please enter your email:");
  const password = currentPassword; //prompt("Please enter your current password:");
  return EmailAuthProvider.credential(email, password);
};

export const updateUser = async (firstName, lastName) => {
  const userRef = doc(db, "users", userId);
  try {
    await updateDoc(userRef, {
      firstName,
      lastName,
    });
  } catch (error) {
    console.error("Error updating user data: ", error);
    throw error;
  }
};

export const getUserData = async (userId) => {
  const userRef = doc(db, "users", userId);
  try {
    const docSnap = await getDoc(userRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    console.error("No such document!");
    return null;
  } catch (error) {
    console.error("Error getting user data: ", error);
    return null;
  }
};

export const handleCreateAnonymousUser = async (timeout) => {
  try {
    setCookie("sessionTimeout", timeout, 30);
    await signInAnonymously(auth)
      .then(async (userCredential) => {
        const user = auth.currentUser;
        if (user) {
          const tokenResult = await user.getIdTokenResult();
          const authTime = tokenResult.claims.auth_time;
          setCookie("sessionTimeout", timeout, 30);
          if (!getCookie("originalAuthTime")) {
            setCookie("originalAuthTime", authTime, 30);
          }
        }
      })
      .catch((error) => {
        console.error("Error creating anonymous user: ", error);
      });
  } catch (error) {
    console.error("Error creating anonymous user: ", error);
    throw error;
  }
};
